import { Button, mergeClasses } from '@fluentui/react-components';
import {
    ArrowNext24Regular,
    ArrowPrevious24Regular,
    MoreHorizontal24Regular,
} from '@fluentui/react-icons';
import React, { useCallback } from 'react';
import usePagination from '../../hooks/usePagination';
import { usePaginationBarStyles } from './PaginationBar.styles';

export interface IPaginationNodeProps {
    pagesCount: number;
    currentPage: number;
    onChange?: (page: number) => void;
}

const PaginationNode: React.FC<IPaginationNodeProps> = ({
    pagesCount,
    currentPage,
    onChange,
}) => {
    const styles = usePaginationBarStyles();

    const onChangeCallback = useCallback(
        (_: unknown, page: number) => {
            if (onChange && page !== currentPage) {
                onChange(page);
            }
        },
        [onChange, currentPage]
    );

    const { items } = usePagination({
        count: pagesCount,
        page: currentPage,
        hideNextButton: true,
        hidePrevButton: true,
        onChange: onChangeCallback,
    });

    if (pagesCount <= 1) {
        return null;
    }

    return (
        <>
            {items.map(({ page, type, selected, ...item }, index) => {
                let children: React.ReactElement | null = null;
                let itemClassName: string | undefined = undefined;

                if (type === 'start-ellipsis' || type === 'end-ellipsis') {
                    children = <MoreHorizontal24Regular />;
                    itemClassName = styles.ellipsis;
                } else if (type === 'page') {
                    children = (
                        <Button
                            appearance={selected ? 'primary' : 'secondary'}
                            {...item}
                        >
                            {page}
                        </Button>
                    );
                } else {
                    const icon =
                        type === 'previous' ? (
                            <ArrowPrevious24Regular />
                        ) : (
                            <ArrowNext24Regular />
                        );
                    children = <Button {...item} icon={icon} />;
                }

                return (
                    <div
                        key={index}
                        className={mergeClasses(styles.item, itemClassName)}
                    >
                        {children}
                    </div>
                );
            })}
        </>
    );
};

export default PaginationNode;
